<template>
  <div>
    <Popover
      v-model="desktopOpen"
      class="h-full w-full"
      hide-on-mobile
      no-padding
      custom-action-handler
    >
      <template #action>
        <button
          class="people-option__button"
          :class="buttonClasses"
          @click="toggleOpen"
          @keydown.space="toggleOpen"
        >
          <TagIcon
            :icon="PeopleIcon"
            color="mono-black"
            class="mr-2 sm:hidden w-5 h-5"
          />
          {{ label }}
          <TagIcon
            :icon="ChevronDownIcon"
            color="mono-black"
            class="ml-1 h-4 w-4"
          />
        </button>
      </template>

      <div class="p-6">
        <LayoutOptions
          style="width: 400px"
          :form="form"
          @change="change"
        />
      </div>

      <ClearAndSubmit
        :submit-label="submitLabel"
        :is-loading="isLoading"
        @clear="clear"
        @submit="submit"
      />
    </Popover>

    <MobilePopover
      v-if="mobileOpen"
      title="People"
      lock-background-scroll
      data-test-id="people-search-filter--mobile"
      @close="submit"
    >
      <LayoutOptions
        :form="form"
        @change="change"
      />

      <template #actions>
        <ClearAndSubmit
          :submit-label="submitLabel"
          :is-loading="isLoading"
          @clear="clear"
          @submit="submit"
        />
      </template>
    </MobilePopover>
  </div>
</template>

<script>
import ChevronDownIcon from '@tagvenue/icons/24/bold/arrows/chevron-down.svg';
import ChairIcon from '@tagvenue/icons/24/bold/education/sitting-chair.svg';
import StandingIcon from '@tagvenue/icons/24/bold/users/user-standing.svg';
import PeopleIcon from '@tagvenue/icons/24/line/users/users-03.svg';
import { markRaw } from 'vue';

import TagIcon from '@/components/TagIcon.vue';
import MobilePopover from '@/components/Ui/MobilePopover/MobilePopover.vue';
import Popover from '@/components/Ui/Popover.vue';
import ClearAndSubmit from '@/modules/public/search/components/SearchFilters/components/ClearAndSubmit.vue';

import SearchOptionShared from '../SearchOptionShared.js';
import LayoutOptions from './LayoutOptions.vue';

export default {
  name: 'PeopleOption',
  mixins: [SearchOptionShared],
  components: {
    ClearAndSubmit,
    Popover,
    TagIcon,
    MobilePopover,
    LayoutOptions,
  },
  data() {
    return {
      PeopleIcon: markRaw(PeopleIcon),
      ChevronDownIcon: markRaw(ChevronDownIcon),
      ChairIcon: markRaw(ChairIcon),
      StandingIcon: markRaw(StandingIcon),
    };
  },
  computed: {
    form() {
      return {
        people: `${this.$store.state.filters.people.value || ''}`,
        roomLayout: this.$store.state.filters.layout.value,
      };
    },
    label() {
      if (parseInt(this.form.people, 10) > 0) {
        if (this.isMobile) {
          return this.form.people;
        }
        if (parseInt(this.form.people, 10) === 1) {
          return `${this.form.people} Person`;
        }
        return `${this.form.people} People`;
      }
      return 'People';
    },
    isSelected() {
      return this.form.people || this.form.roomLayout;
    },
  },
  watch: {
    '$store.state.triggerSection': {
      handler(value) {
        if (value === 'people') {
          this.desktopOpen = true;
        }
      },
      immediate: true,
    },
  },
  methods: {
    clear() {
      this.$store.commit('peopleChange', 0);
      this.$store.commit('layoutChange', 0);
      this.apply({ name: 'RemoveFilter', value: 'people' });
    },
    change(key, value) {
      if (key === 'people') {
        const parsedPeopleValue = Math.floor(value);
        this.$store.commit('peopleChange', parsedPeopleValue);
        this.apply({ name: 'People', value: parsedPeopleValue }, { method: 'debounce', delay: 700 });
      } else if (key === 'roomLayout') {
        this.$store.commit('layoutChange', value);
        this.apply({ name: 'Layout', value });
      }
    },
    openFilter() {
      if (!this.$store.state.rooms.length) {
        return;
      }
      if (this.$store.state.triggerSection && this.$store.state.triggerSection !== 'people') {
        return;
      }
      if (this.isMobile) {
        return;
      }
      if (this.$store.state.highlightedRoomId) {
        return;
      }

      this.desktopOpen = true;
    },
  },
  mounted() {
    window.addEventListener('scroll', this.closeOnScrollThrottled);

    if (this.form.people || (this.$store.state.eventCategory === 'production' && !this.$store.state.filters.date?.value)) {
      return;
    }

    this.$nextTick(() => {
      this.openFilter();
    });
  },
  unmounted() {
    window.removeEventListener('scroll', this.closeOnScrollThrottled);
  },
};

</script>

<style lang="scss" scoped>
@use '../optionButton';

.option__button {
  @include optionButton.optionButton;
}

</style>
